import { createSlice, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Asset, BridgeElement, Span, Task } from "~/types/bridge";
import { fixAndParseJson, onlyUnique } from "~/utils/utils";
import _ from "lodash";
import { fetchAsset } from "~/thunks/assetsThunks";
import { updateBulkThunk, updateSingleThunk } from "~/thunks/updateThunks";
import type { RootState } from "~/store";
import { bulkCreateThunk, createModelAndElementsThunk } from "~/thunks/createThunks";
import type { Survey } from "~/types/general";
import { uploadImageThunk } from "~/thunks/uploadThunk";
import { genericDeleteThunk } from "~/thunks/deleteThunks";


interface AssetState {
  asset: Asset | null;
  primaryModel: any | null;
  elements: BridgeElement[];
  spans: Span[];
  tasks: Task[];
  surveys: Survey[];
  loading: boolean;
  error: string | null;
  customFieldsTemplate?: Record<string, any>[];
}
const initialState: AssetState = {
  asset: null,
  primaryModel: null,
  elements: [],
  spans: [],
  surveys: [],
  loading: false,
  error: null,
  tasks: [],
  customFieldsTemplate: [],
};
const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    setAsset: (state, action: PayloadAction<any>) => {
      state.asset = action.payload;
      state.primaryModel = null;
      state.elements = [];
      state.surveys = [];
      state.spans = [];
      state.tasks = [];
      state.loading = false;
      state.error = null;
      state.customFieldsTemplate = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAsset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // .addCase(fetchAssets.fulfilled, (state, action: PayloadAction<any>) => {
      //   state.loading = false;

      // })
      .addCase(fetchAsset.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchAsset fulfilled", action.payload);
        state.loading = false;
        let updatedAsset = _.clone(action.payload.asset);
        let verticalFields = fixAndParseJson(updatedAsset.vertical_fields);
        const updatedSurveys = action.payload.surveys.map((survey: any) => {
        return {
            ...survey,
            // vertical_fields: fixAndParseJson(survey.vertical_fields),
            elements_data: survey.elements_data ? fixAndParseJson(survey.elements_data) : null,
          }
        });
        const updteElementsFixed = action.payload.elements.map((element: any) => {
          return {
            ...element,
            object_id: element.object_id || element.name, // fallback to name if object_id is missing
            // vertical_fields: fixAndParseJson(element.vertical_fields),
          }
        })
        updatedAsset.vertical_fields = verticalFields;
        // console.log("updatedSurveys", updatedSurveys);
        state.asset = updatedAsset;
        state.primaryModel = action.payload.asset.primary_model_id;
        // state.primaryModel = action.payload.model;
        state.elements = updteElementsFixed;
        state.surveys = updatedSurveys;
        state.spans = action.payload.spans;
        state.customFieldsTemplate = action.payload.customFieldsTemplate;
        const tasks = action.payload.tasks.map((task: any) => {
          return {
            ...task,
            sub_tasks: JSON.parse(task.sub_tasks),
          }
        })
        // console.log("assetSlice fetchAsset fulfilled tasks", tasks);
        state.tasks = tasks;
      })
      .addCase(fetchAsset.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createModelAndElementsThunk.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload) {
          console.log("createModelAndElementsThunk fulfilled", action.payload);
          const filteredElements = state.elements.filter(el => !action.payload.saveData.deleteElementsIds.includes(el.id))
          state.elements = [...filteredElements, ...action.payload.saveData.newElements]
          if (action.payload.saveData && action.payload.createdModel.data.id && state.asset){
            // if (state.asset) {
          state.asset.primary_model_id = action.payload.createdModel.data.id;
            // }
          }
        }
      })
      .addCase(updateSingleThunk.fulfilled, (state, action: PayloadAction<{
        dataType: string,
        data: any
      }>) => {

        if (action.payload.dataType == 'survey') {
      
          let updatedSurvey = action.payload.data;
          if (typeof updatedSurvey.elements_data === 'string') {
            updatedSurvey.elements_data = fixAndParseJson(updatedSurvey.elements_data);
          }
          if (typeof updatedSurvey.vertical_fields === 'string') {
            updatedSurvey.vertical_fields = fixAndParseJson(updatedSurvey.vertical_fields);
          } 
          const surveyIndex = state.surveys.findIndex(survey => survey.id === updatedSurvey.id);
          if (surveyIndex !== -1) {
            console.log("Updating survey in assetSlice", updatedSurvey);
            state.surveys[surveyIndex] = {
              ...state.surveys[surveyIndex],
              ...updatedSurvey,
              
            };
          }
        };
        if (action.payload.dataType == 'tasks') {

          let updatedTask = action.payload.data;
          if (typeof updatedTask.sub_tasks === 'string') {
            updatedTask.sub_tasks = fixAndParseJson(updatedTask.sub_tasks);
          }
          const taskIndex = state.tasks.findIndex(task => task.id === updatedTask.id);
          if (taskIndex !== -1) {
            console.log("Updating task in assetSlice", updatedTask);
            state.tasks[taskIndex] = {
              ...state.tasks[taskIndex],
              ...updatedTask,
            };
          }
        }
        if (action.payload.dataType == 'assets' && state.asset && state.asset.bid === action.payload.data.bid) {
  
          let updatedAsset = {
            ...state.asset,
            ...action.payload.data,
          };
          if (action.payload.data.vertical_fields && typeof action.payload.data.vertical_fields === 'string') {
            updatedAsset.vertical_fields = fixAndParseJson(action.payload.data.vertical_fields);
          }
          state.asset = updatedAsset;
        }
        if (action.payload.dataType == 'bridge_elements') {

          const updatedElement = action.payload.data;
          const elementIndex = state.elements.findIndex(el => el.id === updatedElement.id);
          if (elementIndex !== -1) {
            console.log("Updating element in assetSlice", updatedElement);
            state.elements[elementIndex] = {
              ...state.elements[elementIndex],
              ...updatedElement,
            };
         };
        }

      })
      .addCase(updateBulkThunk.fulfilled, (state, action: PayloadAction<{
        dataType: string,
        data: any
      }>) => {
        if (action.payload.dataType == 'bridge_elements') {
          console.log("updateBulkThunk fulfilled bridge_elements", action.payload);
          const updatedElements = action.payload.data;
          state.elements = state.elements.map(el => {
            const updatedEl = updatedElements.find((ue: any) => ue.id === el.id);
            return updatedEl ? { ...el, ...updatedEl } : el;
          });
        };
        if (action.payload.dataType == 'bridge_spans') {
          console.log("updateBulkThunk fulfilled bridge_spans", action.payload);
          const updatedSpans = action.payload.data;
          state.spans = state.spans.map(span => {
            const updatedSpan = updatedSpans.find((us: any) => us.id === span.id);
            return updatedSpan ? { ...span, ...updatedSpan } : span;
          });
        };

      })
      .addCase(bulkCreateThunk.fulfilled, (state, action: PayloadAction<{
        table: string,
        data: any
      }>) => {
        if (action.payload.table === 'tbl_bridge_spans') {
          console.log("bulkCreateThunk fulfilled bridge_spans", action.payload);
          const newSpans = action.payload.data;
          state.spans = [...state.spans, ...newSpans];
        }
      })
      .addCase(genericDeleteThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("genericDeleteThunk fulfilled", action.payload);
        const operations = action.payload;
        // if (operations.length === 0) return;
        operations.forEach((operation: any) => {
          if (operation.table === 'tbl_bridge_spans') {
            state.spans = state.spans.filter(span => !operation.ids.includes(span.id));
            const affectedElementIds = state.elements.filter(el => operation.ids.includes(el.span_id)).map(el => el.id);
            const updatedElements = state.elements.map(el => {
              if (affectedElementIds.includes(el.id)) {
                return {
                  ...el,
                  span_id: null,
                };
              }
              return el;
            });
            state.elements = updatedElements as BridgeElement[];
          }

        })
      })
      .addCase(uploadImageThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("uploadImageThunk fulfilled", action.payload);
        if (action.payload.dataType === 'assets' && action.payload.data.bid) {
          // Update organization profile image
          if (state.asset && state.asset.bid === action.payload.data.bid) {
            console.log("Updating asset image_url in assetSlice", action.payload.data.image_url);
            state.asset = {
              ...state.asset,
              image_url: action.payload.data.image_url,
            };
          }
        }
      })
      
      
  },
});
export const { setAsset } = assetSlice.actions;
export default assetSlice.reducer;
export const selectAsset = (state: RootState) => state.asset.asset;
export const selectPrimaryModel = (state: RootState) => state.asset.primaryModel;
export const selectAssetCustomFieldsTemplate = (state: RootState) => state.asset.customFieldsTemplate;  
export const selectElements = (state: RootState) => state.asset.elements;
export const selectSurveys = (state: RootState) => state.asset.surveys;
export const selectTasks = (state: RootState) => state.asset.tasks;
export const selectSpans = (state: RootState) => state.asset.spans;
export const getTasksBySurveyId = (state: RootState, surveyId: string) => state.asset.tasks.filter((task: any) => task.survey_id === surveyId);



